// src/components/Navbar.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { ChevronDown, ChevronRight, LogOut, Search, Heart, ShoppingBag, User, X, UserIcon, Menu } from 'lucide-react';
import { useUser } from "@/providers/userContext";
import { getLogo, getStoreTitle } from "@/services/storeService";
import { AxiosPost_Nocatch } from "@/utilities/axioscall";
import { AK } from "@/constants";
import useFCM from "@/app/fireutils/hooks/useFCM";
import SearchBar from "../themes/SearchBar";
import Brand from "@/public/assets/images/Navbar/brand.svg";
import bgoverlay from "@/public/assets/images/Navbar/bgoverlay.png";
import Image from "next/image";
import SvgIcons from "../themes/SvgIcons";
import ComboOffers from "@/public/assets/images/Combo-Offers.png";

const Navbar = ({ categoryListData }: any) => {
  const pathname = usePathname();
  const router = useRouter();
  const { logout, user, cartinfoData } = useUser();
  const { fcmToken, messages, requestPermission, showPrompt } = useFCM();

  const [isMobile, setIsMobile] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [userData, setUserData] = useState<any>(user);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Memoize main categories with display_in_menu === 1
  const mainCategories = useMemo(
    () =>
      categoryListData
        ?.filter(
          (category: { parent_id: number; display_in_menu: number }) =>
            category.parent_id === 0 && category.display_in_menu === 1
        )
        .sort((a: { id: number }, b: { id: number }) => a.id - b.id),
    [categoryListData]
  );

  // Memoize subcategory function
  const getSubcategoriesByParentId = useMemo(
    () => (parentId: number) =>
      categoryListData?.filter(
        (category: any) => category.parent_id === parentId
      ),
    [categoryListData]
  );

  // Handle sidebar animation - FIXED FOR SMOOTH OPEN & CLOSE
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      // Trigger animation after render
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      document.body.style.overflow = "auto";
      setIsAnimating(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = isSearchOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isSearchOpen]);

  // Handle mobile detection and mounting
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    const handleResize = () => setTimeout(checkMobile, 150);
    window.addEventListener("resize", handleResize);
    setHasMounted(true);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update user data and cart count
  useEffect(() => {
    setUserData(user ?? null);
  }, [user]);

  useEffect(() => {
    setCartCount(cartinfoData?.cartcount ?? 0);
  }, [cartinfoData]);

  // Navbar visibility logic
  const hiddenPaths = ["/signin", "/signin/email"];
  const shouldHideNavbar =
    hiddenPaths.includes(pathname) ||
    pathname.startsWith("/cart") ||
    pathname.startsWith("/checkout");

  const handleNavigate = (path: string) => {
    router.push(path);
    setMenuOpen(false);
  };

  const toggleSubcategory = (categoryId: number) => {
    setExpandedCategoryIds((prevIds) =>
      prevIds.includes(categoryId)
        ? prevIds.filter((id) => id !== categoryId)
        : [...prevIds, categoryId]
    );
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const RenderSubcategories = ({ parentId }: { parentId: number }) => {
    const subcategories = getSubcategoriesByParentId(parentId);
    if (!subcategories?.length) return null;

    return (
      <ul className="ml-4 mt-2 border-l border-white/30 pl-4">
        {subcategories.map((subCategory: any) => (
          <li key={subCategory.id} className="py-1">
            <div className="flex justify-between items-center">
              <Link
                href={`/products/${subCategory.seo_url}`}
                className="text-sm text-white hover:text-gray-200"
                onClick={() => setMenuOpen(false)}
              >
                {subCategory.name}
              </Link>
              {getSubcategoriesByParentId(subCategory.id).length > 0 && (
                <button
                  onClick={() => toggleSubcategory(subCategory.id)}
                  className="p-1 text-white hover:text-gray-200 focus:outline-none"
                  aria-expanded={expandedCategoryIds.includes(subCategory.id)}
                  aria-label={`Toggle ${subCategory.name} subcategories`}
                >
                  <ChevronRight
                    className={`w-4 h-4 transition-transform ${expandedCategoryIds.includes(subCategory.id) ? 'rotate-90' : ''}`}
                  />
                </button>
              )}
            </div>

            {expandedCategoryIds.includes(subCategory.id) && (
              <RenderSubcategories parentId={subCategory.id} />
            )}
          </li>
        ))}
      </ul>
    );
  };

  const handleLogout = async () => {
    try {
      const response = await AxiosPost_Nocatch(AK.LOGOUTAPI, {}, true);
      logout();
      router.push("/signin");
    } catch (error: any) {
      console.error("Logout failed:", error.response?.data || error.message);
      logout();
      router.push("/signin");
    }
  };

  const isCartPage = pathname === "/cart";
  const isWishlist = pathname === "/my-wishlist";

  if (!hasMounted || shouldHideNavbar) return null;

  return (
    <>
      <nav className="sticky top-0 z-40 text-white bg-primary">
        {/* Main Navigation Bar */}
        <div className="w-full max-w-[1380px] mx-auto border-b-2 border-dashed border-white/30">
          <div className="flex items-center justify-between px-[24px] py-[10px] ">
            {/* Logo */}
            <div className="flex-shrink-0 lg:w-4/12 w-4/12  md:hidden block">
              <button
                onClick={() => setMenuOpen(true)}
                className="text-white hover:opacity-80 transition-opacity"
                aria-label="Open menu"
              >
                <Menu className="w-7 h-7" />
              </button>
            </div>
            <div className="flex-shrink-0 lg:w-4/12 w-4/12 flex md:justify-start justify-center">
              <Link href="/" className="">
                <Image
                  className="h-[70px] w-auto"
                  width={180}
                  height={70}
                  src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/BO21H1191224/uploads/28ngI4aN7xp7iItASCDZWwOUjfyIUbF4r1Rj7uu8.webp"
                  alt={`${getStoreTitle()}`}
                  priority
                />
              </Link>
            </div>

            {/* Search Bar - Desktop */}
            <div className="hidden lg:block lg:w-4/12 w-4/12">
              <SearchBar isMobile={false} onNavigate={handleNavigate} />
            </div>

            {/* Desktop Navigation Icons */}
            <div className="hidden lg:flex items-center justify-end gap-6 md:w-4/12 w-4/12">
              <Link
                href="/my-wishlist"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <SvgIcons.Wishlisticon />
                <span className="text-sm font-medium">Wishlist</span>
              </Link>

              <Link
                href="/cart"
                className="relative flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <SvgIcons.Bagicon />
                <span className="text-sm font-medium">Shop</span>
                {cartCount > 0 && (
                  <span className="absolute top-[-10px] right-[30px] bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {userData ? (
                <div
                  className="relative"
                  onMouseEnter={() => setIsOpen(true)}
                  onMouseLeave={() => setIsOpen(false)}
                >
                  <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                    <SvgIcons.Usericon />
                    <span className="text-sm font-medium">{userData.name || "Account"}</span>
                  </div>
                  {isOpen && (
                    <div className="absolute right-0 top-full mt-0 w-40 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                      <Link
                        href="/my-account"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsOpen(false)}
                      >
                        My Account
                      </Link>
                      <hr className="border-gray-200" />
                      <button
                        type="button"
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/signin"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <SvgIcons.Usericon />
                  <span className="text-sm font-medium">Login</span>
                </Link>
              )}
            </div>

            {/* Mobile Icons */}
            <div className="flex lg:hidden items-center justify-end gap-4 w-4/12">
              <button
                type="button"
                onClick={toggleSearch}
                aria-label="Open search"
                className="hover:opacity-80 transition-opacity"
              >
                <Search className="w-6 h-6" />
              </button>
              <Link href="/my-wishlist" className="hover:opacity-80 transition-opacity hidden md:block">
                <Heart className="w-6 h-6" />
              </Link>
              <Link href="/cart" aria-label="View cart" className="relative hover:opacity-80 transition-opacity">
                <ShoppingBag className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <Link href={userData ? "/my-account" : "/signin"} className="hover:opacity-80 transition-opacity hidden md:block">
                <SvgIcons.Usericon />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Category Navigation - Desktop & Mobile */}
      <div className="relative bg-primary text-white overflow-hidden">
        <Image
          src={bgoverlay}
          alt=""
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          className="object-cover"
        />
        <div className="relative z-10 w-full max-w-[1380px] mx-auto py-[10px]">
          <div className="flex items-center justify-between px-[24px]">
            {/* Hamburger Menu Icon - Left side */}
            {/* <button
              onClick={() => setMenuOpen(true)}
              className="text-white hover:opacity-80 transition-opacity md:hidden block"
              aria-label="Open menu"
            >
              <Menu className="w-7 h-7" />
            </button> */}

            <div className="w-3/12 hidden md:block">
              <Image
                src={Brand}
                alt="Brand Logo"
                width={160}
                height={60}
                className="h-10 lg:h-12"
              />
            </div>

            <div className="lg:w-4/12 md:w-5/12 w-full mx-auto">
              <div className="flex justify-center items-center space-x-4">
                <Link
                  href='/'
                  className="flex flex-col items-center"
                >
                  <div className="icon-wrapper bg-white lg:p-4 p-2 rounded-[40%] mb-[8px]">
                    <SvgIcons.NavHome />
                  </div>
                  <span className="text-xs font-medium text-center h-[30px]">Home</span>
                </Link>

                {mainCategories?.slice(0, 4).map((category: any, index: number) => {
                  const icons = [
                    <SvgIcons.BabyCareicon key={`icon-${index}`} />,
                    <SvgIcons.BabyToyicon key={`icon-${index}`} />,
                    <SvgIcons.ComboOffer key={`icon-${index}`} />,
                    <SvgIcons.ForMomsicon key={`icon-${index}`} />
                  ];

                  return (
                    <Link
                      key={category.id}
                      href={`/products/${category.seo_url}`}
                      className="flex flex-col items-center"
                    >
                      <div className="bg-white lg:p-4 p-2 rounded-[40%] mb-[8px]">
                        {icons[index] ?? <SvgIcons.ForMomsicon />}
                      </div>

                      <span className="text-xs font-medium text-center h-[30px]">
                        {category.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="w-3/12 hidden md:block">
              <div className="flex items-center gap-4 justify-end">
                <Link href="https://www.youtube.com/@bogi-craftedwithcare" target="_blank" rel="noopener noreferrer" aria-label="Visit our YouTube channel" className="hover:opacity-80 transition-opacity">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </Link>
                <Link href="https://www.instagram.com/bogilifestyles" className="hover:opacity-80 transition-opacity" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </Link>
                <Link href="https://www.facebook.com/bogilife" className="hover:opacity-80 transition-opacity" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={toggleSearch}
          />
          <div className="absolute top-0 left-0 right-0 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <SearchBar isMobile={true} onNavigate={(path) => {
                  handleNavigate(path);
                  toggleSearch();
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar - PERFECTLY SMOOTH OPEN & CLOSE */}
      {menuOpen && (
        <>
          {/* Background Overlay */}
          <div
            className={`fixed inset-0 bg-black z-[9999] transition-opacity duration-500 ease-in-out ${isAnimating ? 'opacity-50' : 'opacity-0'
              }`}
            onClick={() => setMenuOpen(false)}
          />

          {/* Sidebar Content */}
          <div
            className={`fixed top-0 left-0 h-full w-4/5 max-w-[320px] bg-primary px-4 overflow-auto z-[10000] transform transition-transform duration-500 ease-in-out shadow-2xl ${isAnimating ? 'translate-x-0' : '-translate-x-full'
              }`}
            onClick={(e) => e.stopPropagation()}
          >

            <div className="flex flex-col justify-between h-full ">
              <div>
                <div className="sticky top-0 bg-primary pt-4 pb-3">
                  <div className="relative">
                    <Link href="/" className="block py-2" onClick={() => setMenuOpen(false)}>
                      <LazyLoadImage
                        className="mx-auto mb-3"
                        width="130"
                        src={`${getLogo()}`}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://blr1.digitaloceanspaces.com/assetsvilva/vilvabusiness/website/common/otherallimages/empty-logowebp.webp";
                        }}
                        alt={`${getStoreTitle()}`}
                      />
                    </Link>
                    <button
                      className="absolute top-2 right-2 text-white"
                      onClick={() => setMenuOpen(false)}
                      aria-label="Close mobile menu"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center py-2 border-t border-white/20 pt-3">
                    <div className="flex items-center gap-2">
                      <SvgIcons.Usericon />
                      <Link
                        href="/signin"
                        className="text-sm text-white uppercase"
                        onClick={() => setMenuOpen(false)}
                      >
                        {userData ? `Hi ${userData.name}!` : "Sign In"}
                      </Link>
                    </div>
                    <div>
                      <div className="flex items-center gap-4 justify-end">
                        <Link href="https://www.youtube.com/@bogi-craftedwithcare" target="_blank" rel="noopener noreferrer" aria-label="Visit our YouTube channel" className="hover:opacity-80 transition-opacity ">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                          </svg>
                        </Link>
                        <Link href="https://www.instagram.com/bogilifestyles" className="hover:opacity-80 transition-opacity" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </Link>
                        <Link href="https://www.facebook.com/bogilife" className="hover:opacity-80 transition-opacity" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <ul className="text-white">
                  <li className="py-3 border-b border-white/20">
                    <Link
                      href="/"
                      className="text-base"
                      onClick={() => setMenuOpen(false)}
                    >
                      Home
                    </Link>
                  </li>
                  {mainCategories?.length > 0 ? (
                    mainCategories.map((category: any) => (
                      <li className="py-3 border-b border-white/20" key={category.id}>
                        <div className="flex justify-between items-center">
                          <Link
                            href={`/products/${category.seo_url}`}
                            className="text-base"
                            onClick={() => setMenuOpen(false)}
                          >
                            {category.name}
                          </Link>
                          {getSubcategoriesByParentId(category.id).length > 0 && (
                            <button
                              onClick={() => toggleSubcategory(category.id)}
                              className="p-1 text-white focus:outline-none"
                              aria-expanded={expandedCategoryIds.includes(
                                category.id
                              )}
                              aria-label={`Toggle ${category.name} subcategories`}
                            >
                              <ChevronRight
                                className={`w-4 h-4 transition-transform ${expandedCategoryIds.includes(category.id) ? 'rotate-90' : ''}`}
                              />
                            </button>
                          )}
                        </div>
                        {expandedCategoryIds.includes(category.id) && (
                          <RenderSubcategories parentId={category.id} />
                        )}
                      </li>
                    ))
                  ) : (
                    <li className="py-3 text-white">Loading categories...</li>
                  )}
                  {userData && (
                    <>

                      <li className="py-3 border-b border-white/20">
                        <Link
                          href="/my-account"
                          className="text-white"
                          onClick={() => setMenuOpen(false)}
                        >
                          My Account
                        </Link>
                      </li>
                      <li className="py-3 border-b border-white/20">
                        <Link
                          href="/my-wishlist"
                          className="text-white"
                          onClick={() => setMenuOpen(false)}
                        >
                          Wishlist
                        </Link>
                      </li>
                      <li className="py-3 border-b border-white/20">
                        <Link
                          href="/sitemap"
                          className="text-white"
                          onClick={() => setMenuOpen(false)}
                        >
                          Sitemap
                        </Link>
                      </li>
                      {/* <li className="py-3 flex justify-between items-center">
                        <div
                          onClick={() => handleLogout()}
                          className="text-white cursor-pointer"
                        >
                          Log Out
                        </div>
                        <div onClick={() => handleLogout()} className="cursor-pointer">
                          <LogOut className="text-white w-5 h-5" />
                        </div>
                      </li> */}
                    </>
                  )}
                </ul>
              </div>

              <div>
                {userData && (
                  <>
                    <ul>
                      <li className="py-3 flex justify-between items-center">
                        <div
                          onClick={() => handleLogout()}
                          className="text-white cursor-pointer"
                        >
                          Log Out
                        </div>
                        <div onClick={() => handleLogout()} className="cursor-pointer">
                          <LogOut className="text-white w-5 h-5" />
                        </div>
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
